import './theme/global.css';

import { useState, useMemo, useEffect } from 'react';
import Axios from 'axios';
import { HelmetProvider, Helmet } from 'react-helmet-async';

import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from 'react-router-dom';

import { Home } from './components/Home';
import { LoadingLogo } from './components/LoadingLogo';
import { Terms } from './components/Terms';
import { Privacy } from './components/Privacy';
import { MyAccountList } from './components/MyAccount';
import { Navbar } from './components/navbar';
import { Trends } from './components/Trends';
import { AboutUs } from './components/Aboutus';
import { ProdServ } from './components/ProdServ';
import { EngWf } from './components/EngWf';
import { SearchResults } from './components/SearchResults';
import { TrendsList } from './components/TrendsList';
import { EngInfo } from './components/EngInfo';
import { Login } from './components/Login';
import { ContactNow } from './components/ContactUs';
import { NewSignup } from './components/NewSignUp';
import { Careers } from './components/Careers';
import { SingleCareer } from './components/SingleCareer';
import { Ackdg } from './components/Acknowledgments';
import { FAQs } from './components/FAQs';
import { ConversionsList } from './components/ConversionsList';
import { Footer } from './components/Footer';
import { QandA } from './components/QandA';
import { AwardsAndNews } from './components/AwardsAndNews';
import { SingleAwardAndNew } from './components/SingleAwardAndNews';
import { TutorialVideos } from './components/TutorialVideos';
import { NewLoginForm } from './components/NewLoginDailogForm';
import { NotFound, ServerError, Forbidden, Offline } from './components/errors/ErrorPage';

import { UserContext, SnackbarContext, EngInfoContext } from './components/userContext';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

/* ─────────── inline icons ─────────── */
const I = {
  ArrowUp: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  ),
  Check: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12l5 5L20 7" />
    </svg>
  ),
  Alert: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  Info: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  Close: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ),
};

/* ═══════════════════ Snackbar (Tailwind) ═══════════════════ */

const SNACK_TYPES = {
  success: { icon: I.Check, color: 'text-amber',  ringColor: 'border-amber/50',     glow: 'shadow-[0_8px_28px_-8px_rgba(251,191,36,0.5)]', accentBg: 'bg-amber/15' },
  error:   { icon: I.Alert, color: 'text-red-400', ringColor: 'border-red-400/50',  glow: 'shadow-[0_8px_28px_-8px_rgba(248,113,113,0.45)]', accentBg: 'bg-red-400/15' },
  warning: { icon: I.Alert, color: 'text-amber',   ringColor: 'border-amber/50',    glow: 'shadow-[0_8px_28px_-8px_rgba(251,191,36,0.4)]',  accentBg: 'bg-amber/15' },
  info:    { icon: I.Info,  color: 'text-amber',   ringColor: 'border-amber/40',    glow: 'shadow-[0_8px_24px_-8px_rgba(0,0,0,0.6)]',       accentBg: 'bg-amber/10' },
};

function GlobalSnackbar({ snack, setSnack }) {
  useEffect(() => {
    if (!snack.open) return undefined;
    const t = setTimeout(() => {
      setSnack((prev) => ({ ...prev, open: false }));
    }, 4000);
    return () => clearTimeout(t);
  }, [snack.open, snack.message, setSnack]);

  if (!snack.open) return null;

  const meta = SNACK_TYPES[snack.type] || SNACK_TYPES.info;
  const Icon = meta.icon;
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;

  const positionClasses = isMobile
    ? 'left-3 right-3 top-3 mx-auto max-w-[480px] animate-[slideInDown_280ms_cubic-bezier(0.16,1,0.3,1)]'
    : 'right-5 bottom-5 max-w-[400px] animate-[slideInRight_280ms_cubic-bezier(0.16,1,0.3,1)]';

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed z-[1500] ${positionClasses}`}
    >
      <div
        className={`
          relative flex items-start gap-3 overflow-hidden
          rounded-2xl border ${meta.ringColor} bg-surface-paper
          ${meta.glow}
          px-4 py-3
        `}
      >
        <span aria-hidden className={`pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-amber/60 to-transparent`} />
        <span aria-hidden className={`absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full ${snack.type === 'error' ? 'bg-red-400' : 'bg-amber'}`} />

        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${meta.accentBg} ${meta.color}`}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="flex-1 min-w-0 pt-0.5 text-[13px] leading-[1.5] text-fg-primary">
          {snack.message}
        </div>
        <button
          type="button"
          onClick={() => setSnack((prev) => ({ ...prev, open: false }))}
          aria-label="Dismiss"
          className="
            inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md
            text-fg-tertiary
            transition-[background-color,color] duration-200
            hover:bg-surface-overlay hover:text-amber
          "
        >
          <I.Close className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════ Back-to-top FAB ═══════════════════ */

function ScrollTopFab() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 200);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleClick = () => {
    const anchor = document.getElementById('back-to-top-anchor');
    if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Scroll back to top"
      className={`
        fixed right-5 bottom-5 z-[1000]
        inline-flex h-11 w-11 items-center justify-center rounded-full
        border border-amber/40 bg-amber/12 text-amber
        backdrop-blur-md
        transition-[opacity,transform,background-color,box-shadow] duration-300 ease-out-soft
        hover:bg-amber/24 hover:-translate-y-0.5
        hover:shadow-[0_0_0_3px_rgba(251,191,36,0.18),0_10px_28px_-8px_rgba(251,191,36,0.5)]
        ${visible ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-50 pointer-events-none'}
      `}
    >
      <I.ArrowUp className="h-5 w-5" />
    </button>
  );
}

/* ═══════════════════ App ═══════════════════ */

function App() {
  const [user, setUser] = useState(null);
  const [snack, setSnack] = useState({
    message: '',
    type: 'success',
    open: false,
    direction: 'right',
  });
  const [engInfoList, setEngInfoList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState();
  const [subCats, setSubCats] = useState();
  const [showLoginForm] = useState(false);

  const userValue = useMemo(() => ({ user, setUser }), [user]);
  const snackValue = useMemo(() => ({ snack, setSnack }), [snack]);
  const enginfoValue = useMemo(() => ({ engInfoList, setEngInfoList }), [engInfoList]);

  useEffect(() => {
    getdata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getdata = async () => {
    const tkn = localStorage?.getItem('estkn');

    // Geolocation runs in the background — never block app boot on it.
    getLocation();

    // Categories run in parallel with the session check — also non-blocking.
    Axios.get('/new_apis/get-cats-data', { headers: { key } }).then((res) => {
      setCats(res?.data?.cats);
      setSubCats(res?.data?.subcats);
    }).catch(() => { /* keep app booting even if cats fail */ });

    // No token? render as guest immediately — don't waste a round-trip.
    if (!tkn) {
      setLoading(false);
      return;
    }

    // Validate the token. The token is ONLY removed on an explicit
    // "invalid / expired" signal from the server (status === 4). Transient
    // failures (status: false, network errors) leave the token in place so
    // the next reload can retry — that way one bad response doesn't log
    // the user out forever.
    try {
      const res = await Axios.get('/new_apis/get-new-session', { headers: { tkn } });
      if (res?.data?.status) {
        setUser(res.data.data);
        getEngInfodata(res?.data?.data?.token || tkn, res.data.data?.user_id);
      } else if (res?.data?.status === 4 || res?.data?.tokenExpired) {
        // Server says the token is no longer valid — clear it.
        localStorage.removeItem('estkn');
      }
      // Any other non-success: keep the token, render as guest for now.
    } catch (err) {
      // Network / 5xx / CORS — keep the token, render as guest.
      // eslint-disable-next-line no-console
      console.warn('[ESAY] session check failed, keeping stored token:', err?.message || err);
    } finally {
      setLoading(false);
    }
  };

  const getEngInfodata = async (tkn, uid) => {
    Axios.post('/new_apis/get_bookmarks', { user_id: uid }, { headers: { tkn } }).then((res) => {
      if (res.data.status === 1) setEngInfoList(res.data.data.map((d) => d.id));
    });
  };

  const getLocation = async () => {
    await navigator.geolocation.getCurrentPosition(async (position) => {
      if (position !== undefined) {
        const lat = position.coords.latitude;
        const long = position.coords.longitude;
        const data = await Axios.get('/new_apis/get-current-location', {
          params: { lat, lag: long },
          headers: { key },
        });
        localStorage.setItem('loc', data.data.loc);
      } else {
        localStorage.setItem('loc', 'Hyderabad, Telangana, India');
      }
    }, () => {
      localStorage.setItem('loc', 'Hyderabad, Telangana, India');
    });
  };

  return (
    <HelmetProvider>
      <div className="min-h-screen flex flex-col bg-surface-base text-fg-primary">
        <GlobalSnackbar snack={snack} setSnack={setSnack} />

        {loading ? (
          <LoadingLogo />
        ) : (
          <SnackbarContext.Provider value={snackValue}>
            <UserContext.Provider value={userValue}>
              <EngInfoContext.Provider value={enginfoValue}>
                <Router>
                  <Navbar cats={cats} subcats={subCats} />
                  <main className="flex-1">
                    <NewLoginForm open={showLoginForm} />
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/acknowledgments" element={<Ackdg />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/about" element={<AboutUs />} />
                      <Route path="/community-qa" element={<QandA />} />
                      <Route path="/contact-now" element={<ContactNow />} />
                      <Route path="/privacy_policy" element={<Privacy />} />
                      <Route path="/awards_news" element={<AwardsAndNews />} />
                      <Route path="/esay_tutorials_walkthroughs" element={<TutorialVideos />} />
                      <Route path="/faqs" element={<FAQs />} />

                      <Route
                        path="/login"
                        element={
                          user
                            ? <Navigate to="/" />
                            : <><Helmet><title>Login | ESAY - Engineering Sources Around You</title></Helmet><Login /></>
                        }
                      />
                      <Route
                        path="/signup"
                        element={user ? <Navigate to="/" /> : <NewSignup />}
                      />
                      <Route
                        path="/myaccount/*"
                        element={
                          user
                            ? <><Helmet><title>My Account | ESAY - Engineering Sources Around You</title></Helmet><MyAccountList /></>
                            : <Navigate to="/" />
                        }
                      />

                      <Route path="/trends" element={<Trends />} />
                      <Route path="/eng-info/*" element={<EngInfo />} />
                      <Route path="/careers" element={<Careers />} />
                      <Route path="/trends-list/*" element={<TrendsList />} />
                      <Route path="/conversions" element={<Navigate to="/conversions-list/length" replace />} />
                      <Route path="/conversions-list/*" element={<ConversionsList />} />
                      <Route path="/searchresults/:locn?/:catn?/:subcat?/:searchn?" element={<SearchResults />} />
                      <Route path="/psdetails/:slug?/:id?" element={<ProdServ />} />
                      <Route path="/job-details/:slug?/:id?" element={<SingleCareer />} />
                      <Route path="/ewdetails/:slug?/:id?" element={<EngWf />} />
                      <Route path="/article-details/:slug?/:id?" element={<SingleAwardAndNew />} />

                      <Route path="/403" element={<><Helmet><title>Access denied | ESAY</title></Helmet><Forbidden /></>} />
                      <Route path="/500" element={<><Helmet><title>Something went wrong | ESAY</title></Helmet><ServerError /></>} />
                      <Route path="/offline" element={<><Helmet><title>Offline | ESAY</title></Helmet><Offline /></>} />
                      <Route path="*" element={<><Helmet><title>Page not found | ESAY</title></Helmet><NotFound /></>} />
                    </Routes>
                  </main>
                  <Footer />
                  <ScrollTopFab />
                </Router>
              </EngInfoContext.Provider>
            </UserContext.Provider>
          </SnackbarContext.Provider>
        )}
      </div>
    </HelmetProvider>
  );
}

export default App;
